import React, { useEffect, useMemo, useState } from "react";
import { confirmDialog, promptDialog } from "../../utils/toast";

// 관리자가 복사해서 안내하는 공개 스트림 주소.
// 접속 중인 대시보드 호스트명으로 파생 — 외부에서 접속해 보면 공인 IP,
// 내부망에서 보면 내부 IP가 표시된다. (PC IP 하드코딩 금지)
const STREAM_HOST =
  typeof window !== "undefined" ? window.location.hostname : "127.0.0.1";

const PUBLIC_RTSP_BASE = `rtsp://${STREAM_HOST}:8554/live`;

const USER_PAGE_SIZE = 9;
const CAM_LIST_PAGE_SIZE = 5;

export default function AdminCamerasPage({ me }) {
  // 시스템 전체 라이선스(플랜/등록 카메라/남은 슬롯)는 슈퍼어드민만 볼 수 있다.
  const isSuperAdmin =
    String(me?.roleCode || "").trim().toUpperCase() === "SUPER_ADMIN";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);

  const [selectedUserIdx, setSelectedUserIdx] = useState("");
  const [camNo, setCamNo] = useState("");
  const [camTitle, setCamTitle] = useState("");
  const [sourceRtsp, setSourceRtsp] = useState("");
  const [saving, setSaving] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [camListPage, setCamListPage] = useState(1);
  const [showCamForm, setShowCamForm] = useState(false);

  const getEffectiveIsActive = (camera) =>
    !!(
      camera?.isActive ||
      (camera?.streamKey && String(camera.streamKey).trim()) ||
      (camera?.streamPath && String(camera.streamPath).trim())
    );

  const makeRtspUrl = (streamKey) =>
    streamKey ? `${PUBLIC_RTSP_BASE}/${streamKey}` : "-";

  const refreshLicenseStatus = async () => {
    try {
      setLicenseLoading(true);
      const res = await fetch("/api/license/status", {
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => null);
      setLicenseStatus(
        data || {
          ok: false,
          reason: "LICENSE_STATUS_EMPTY",
          message: "라이선스 상태 응답이 비어 있습니다.",
        }
      );
      return data;
    } catch (err) {
      const fallback = {
        ok: false,
        reason: "LICENSE_STATUS_FETCH_ERROR",
        message: "라이선스 상태를 불러오지 못했습니다.",
        error: err?.message || String(err),
      };
      setLicenseStatus(fallback);
      return fallback;
    } finally {
      setLicenseLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setMsg(data.error || data.message || "사용자/카메라 목록 조회 실패");
        setUsers([]);
        return;
      }
      const grouped = new Map();
      (data.users || []).forEach((row) => {
        if (!grouped.has(row.UserIdx)) {
          grouped.set(row.UserIdx, {
            userIdx: row.UserIdx,
            userId: row.UserID,
            userName: row.UserName,
            companyName: row.CompanyName || "",
            userPathKey: row.UserPathKey,
            roleCode: row.RoleCode,
            approvalStatus: Number(row.ApprovalStatus || 0),
            maxCameraCount: Number(row.MaxCameraCount || 3),
            cameras: [],
          });
        }
        if (row.CamIdx) {
          grouped.get(row.UserIdx).cameras.push({
            camIdx: row.CamIdx,
            camNo: row.CamNo,
            camTitle: row.CamTitle,
            sourceRtsp: row.SourceRTSP || "",
            streamKey: row.StreamKey || "",
            streamPath: row.StreamPath || "",
            streamUrl: row.StreamUrl || "",
            isActive:
              row.IsActive === undefined || row.IsActive === null
                ? true
                : !!row.IsActive,
            aiEnabled:
              row.AiEnabled === undefined || row.AiEnabled === null
                ? true
                : !!row.AiEnabled,
            createdAt: row.CameraCreatedAt || row.CreatedAt || "",
          });
        }
      });
      // 카메라 관리에는 승인 완료(ApprovalStatus=1)된 계정만 노출한다.
      // 미승인 계정은 "승인 관리"에서만 다룬다.
      const list = Array.from(grouped.values()).filter(
        (u) => u.approvalStatus === 1
      );
      setUsers(list);
      if (!selectedUserIdx && list.length > 0) {
        setSelectedUserIdx(String(list[0].userIdx));
      }
    } catch (err) {
      setMsg("사용자/카메라 목록을 불러오는 중 오류가 발생했습니다.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshLicenseStatus();
    loadUsers();
  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.userIdx) === String(selectedUserIdx)) || null,
    [users, selectedUserIdx]
  );

  const totalRegisteredCameras = useMemo(
    () => users.reduce((sum, u) => sum + (u.cameras?.length || 0), 0),
    [users]
  );

  const licenseInfo = useMemo(() => {
    const license = licenseStatus?.license || {};
    const maxDevices = Number(license.maxDevices || 0);
    const remainingDevices =
      maxDevices > 0 ? Math.max(maxDevices - totalRegisteredCameras, 0) : null;
    return {
      ok: licenseStatus?.ok === true,
      reason: licenseStatus?.reason || "",
      message: licenseStatus?.message || "",
      plan: String(license.plan || "-"),
      maxDevices,
      remainingDevices,
      // 이 서버가 «서버 전체 총량»을 실제로 강제하는지. 서버가 알려준다.
      //   본사 서버는 여러 고객사를 함께 받으므로 총량이 의미 없어 false 가 온다
      //   (실제 통제선은 회사별 구독 한도). 납품 서버는 true 라 예전과 동일하게 막힌다.
      deviceLimitEnforced: licenseStatus?.deviceLimitEnforced !== false,
    };
  }, [licenseStatus, totalRegisteredCameras]);

  const editingExistingCamera = useMemo(() => {
    if (!selectedUser || !camNo) return false;
    return selectedUser.cameras.some((c) => String(c.camNo) === String(camNo));
  }, [selectedUser, camNo]);

  /*
    계정별 카메라 상한 — 2026-10-13 정리.

    예전에는 모든 계정에 «기본 3대» 상한이 걸려 있어, 회사 한도가 남아 있어도
    관리자 계정에서 카메라를 더 못 만들었다. 한도가 두 겹이라 어느 쪽에 걸렸는지도 몰랐다.

    이제 실제 통제선은 «회사 구독 한도»다. 화면에서는 작업자(1인 1대)만 막고,
    그 외 계정은 서버 판단에 맡긴다(초과하면 서버가 사유와 함께 거부한다).
  */
  const selectedUserCameraLimitExceeded = useMemo(() => {
    if (!selectedUser || editingExistingCamera) return false;
    const role = String(selectedUser.roleCode || "").toUpperCase();
    const isWorker = role === "WORKER" || role === "INSTALLER";
    if (!isWorker) return false;
    return selectedUser.cameras.length >= 1;
  }, [selectedUser, editingExistingCamera]);

  const globalLicenseDeviceLimitExceeded = useMemo(() => {
    if (editingExistingCamera) return false;
    // 서버가 총량을 강제하지 않으면 화면도 막지 않는다.
    //   예전에는 화면만 막고 서버는 허용해, 버튼이 안 눌리는데 이유를 알 수 없었다.
    if (!licenseInfo.deviceLimitEnforced) return false;
    if (!licenseInfo.maxDevices || licenseInfo.maxDevices <= 0) return false;
    return totalRegisteredCameras >= licenseInfo.maxDevices;
  }, [
    editingExistingCamera,
    licenseInfo.deviceLimitEnforced,
    licenseInfo.maxDevices,
    totalRegisteredCameras,
  ]);

  const canSaveCamera = useMemo(() => {
    if (saving) return false;
    if (!licenseInfo.ok) return false;
    if (globalLicenseDeviceLimitExceeded) return false;
    if (selectedUserCameraLimitExceeded) return false;
    return true;
  }, [saving, licenseInfo.ok, globalLicenseDeviceLimitExceeded, selectedUserCameraLimitExceeded]);

  // 사용자 목록 검색/페이지
  const filteredUsers = useMemo(() => {
    const q = String(userSearch || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [u.userId, u.userName, u.companyName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [users, userSearch]);

  const userPageCount = Math.max(1, Math.ceil(filteredUsers.length / USER_PAGE_SIZE));
  const pagedUsers = useMemo(() => {
    const safePage = Math.min(Math.max(userPage, 1), userPageCount);
    const start = (safePage - 1) * USER_PAGE_SIZE;
    return filteredUsers.slice(start, start + USER_PAGE_SIZE);
  }, [filteredUsers, userPage, userPageCount]);

  useEffect(() => {
    setUserPage(1);
  }, [userSearch]);
  useEffect(() => {
    if (userPage > userPageCount) setUserPage(userPageCount);
  }, [userPage, userPageCount]);

  // 선택 사용자의 카메라 목록 (번호순) + 페이지네이션
  const sortedCameras = useMemo(() => {
    const list = selectedUser?.cameras || [];
    return list.slice().sort((a, b) => Number(a.camNo) - Number(b.camNo));
  }, [selectedUser]);

  const camListPageCount = Math.max(
    1,
    Math.ceil(sortedCameras.length / CAM_LIST_PAGE_SIZE)
  );
  const pagedCameras = useMemo(() => {
    const safePage = Math.min(Math.max(camListPage, 1), camListPageCount);
    const start = (safePage - 1) * CAM_LIST_PAGE_SIZE;
    return sortedCameras.slice(start, start + CAM_LIST_PAGE_SIZE);
  }, [sortedCameras, camListPage, camListPageCount]);

  // 다른 사용자를 선택하면 카메라 목록 1페이지로
  useEffect(() => {
    setCamListPage(1);
  }, [selectedUserIdx]);
  useEffect(() => {
    if (camListPage > camListPageCount) setCamListPage(camListPageCount);
  }, [camListPage, camListPageCount]);

  const resetForm = () => {
    setCamNo("");
    setCamTitle("");
    setSourceRtsp("");
  };

  const copyText = async (text, label = "복사") => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} 완료`);
    } catch (err) {
      alert(`${label} 실패`);
    }
  };

  const handleEditCamera = (camera) => {
    setCamNo(String(camera.camNo || ""));
    setCamTitle(camera.camTitle || "");
    setSourceRtsp(camera.sourceRtsp && camera.sourceRtsp !== "." ? camera.sourceRtsp : "");
    setShowCamForm(true);
  };

  const openNewCameraForm = () => {
    resetForm();
    setShowCamForm(true);
  };

  const handleSaveCamera = async () => {
    if (!selectedUserIdx || !camNo || !camTitle) {
      alert("사용자, 카메라 번호, 카메라명을 모두 입력해주세요.");
      return;
    }
    if (!licenseInfo.ok) {
      alert(licenseInfo.message || licenseInfo.reason || "라이선스가 정상 상태가 아니므로 카메라를 저장할 수 없습니다.");
      return;
    }
    if (globalLicenseDeviceLimitExceeded) {
      alert(`라이선스 허용 단말기 수를 초과했습니다.\n현재 ${totalRegisteredCameras}대 / 최대 ${licenseInfo.maxDevices}대`);
      return;
    }
    if (selectedUserCameraLimitExceeded) {
      alert(`선택한 사용자의 최대 카메라 수를 초과했습니다.\n현재 ${selectedUser.cameras.length}대 / 최대 ${selectedUser.maxCameraCount}대`);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/admin/camera", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userIdx: Number(selectedUserIdx),
          camNo: Number(camNo),
          camTitle,
          sourceRtsp: sourceRtsp.trim(),
          isActive: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "카메라 저장 실패");
        return;
      }
      alert("카메라 저장 완료");
      resetForm();
      setShowCamForm(false);
      await loadUsers();
      await refreshLicenseStatus();
    } catch (err) {
      alert("카메라 저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCamera = async (userIdx, camera) => {
    if (
      !(await confirmDialog(
        `[${camera.camTitle || `CAM ${camera.camNo}`}] 카메라를 삭제하시겠습니까?`,
        { title: "카메라 삭제", danger: true, confirmText: "삭제" }
      ))
    )
      return;
    try {
      const res = await fetch("/api/admin/camera/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx, camNo: camera.camNo }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "카메라 삭제 실패");
        return;
      }
      alert("카메라 삭제 완료");
      await loadUsers();
      await refreshLicenseStatus();
    } catch (err) {
      alert("카메라 삭제 중 오류가 발생했습니다.");
    }
  };

  const handleToggleActive = async (userIdx, camera) => {
    const currentActive = getEffectiveIsActive(camera);
    try {
      const res = await fetch("/api/admin/camera/active", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx, camNo: camera.camNo, isActive: !currentActive }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "카메라 활성 상태 변경 실패");
        return;
      }
      loadUsers();
    } catch (err) {
      alert("카메라 활성 상태 변경 중 오류가 발생했습니다.");
    }
  };

  const handleToggleAi = async (userIdx, camera) => {
    const next = !camera.aiEnabled;
    try {
      const res = await fetch("/api/admin/camera/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx, camNo: camera.camNo, aiEnabled: next }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "카메라 AI 설정 변경 실패");
        return;
      }
      loadUsers();
    } catch (err) {
      alert("카메라 AI 설정 변경 중 오류가 발생했습니다.");
    }
  };

  const handleChangeMaxCameraCount = async (userIdx, maxCameraCount) => {
    const value = await promptDialog(
      "최대 등록 가능 카메라 수를 입력하세요.",
      String(maxCameraCount),
      { title: "최대 카메라 수", inputType: "number", confirmText: "저장" }
    );
    if (value === null) return;
    const next = Number(value);
    if (!Number.isInteger(next) || next < 1) {
      alert("1 이상의 숫자만 입력할 수 있습니다.");
      return;
    }
    try {
      const res = await fetch("/api/admin/max-camera-count", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx, maxCameraCount: next }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "최대 카메라 수 저장 실패");
        return;
      }
      alert("최대 카메라 수 저장 완료");
      loadUsers();
    } catch (err) {
      alert("최대 카메라 수 저장 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1">
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">카메라 관리</h2>

        {msg && (
          <div className="rounded-xl bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {msg}
          </div>
        )}

        {/* 라이선스 현황 (컴팩트) — 슈퍼어드민 전용 */}
        {isSuperAdmin && (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  licenseInfo.ok ? "bg-emerald-500" : "bg-red-500"
                }`}
              />
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                라이선스 {licenseInfo.ok ? "정상" : "오류"}
              </span>
            </div>
            <Stat
              label="등록 카메라"
              value={
                licenseInfo.maxDevices > 0
                  ? `${totalRegisteredCameras} / ${licenseInfo.maxDevices}대`
                  : `${totalRegisteredCameras}대`
              }
            />
            {/* 총량을 강제하지 않는 서버에서는 «남은 슬롯»이 뜻이 없다(0으로만 보여 오해를 준다). */}
            {licenseInfo.deviceLimitEnforced && licenseInfo.remainingDevices !== null && (
              <Stat label="남은 슬롯" value={`${licenseInfo.remainingDevices}대`} />
            )}
            <button
              type="button"
              onClick={refreshLicenseStatus}
              disabled={licenseLoading}
              className="ml-auto rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60 dark:bg-white/10 dark:text-gray-200"
            >
              {licenseLoading ? "확인 중..." : "라이선스 새로고침"}
            </button>
          </div>
          {!licenseInfo.ok && (licenseInfo.message || licenseInfo.reason) && (
            <div className="mt-2 text-xs text-red-600 dark:text-red-300">
              {licenseInfo.message || licenseInfo.reason}
            </div>
          )}
        </section>
        )}

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
              불러오는 중...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:min-h-[460px] lg:grid-cols-12">
              {/* 좌측: 사용자 목록 */}
              <div className="lg:col-span-4">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="아이디 / 회사 검색"
                  className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />
                <div className="space-y-1.5">
                  {filteredUsers.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                      {userSearch ? "검색 결과가 없습니다." : "사용자가 없습니다."}
                    </div>
                  ) : (
                    pagedUsers.map((u) => {
                      const active = String(u.userIdx) === String(selectedUserIdx);
                      return (
                        <button
                          key={u.userIdx}
                          type="button"
                          onClick={() => {
                            setSelectedUserIdx(String(u.userIdx));
                            resetForm();
                          }}
                          className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                            active
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="line-clamp-1 text-sm font-semibold text-gray-900 dark:text-white">
                              {u.userId}
                            </div>
                            <div className="line-clamp-1 text-xs text-gray-500 dark:text-gray-400">
                              {u.companyName || u.userName || "-"}
                            </div>
                          </div>
                          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                            {u.cameras.length}/{u.maxCameraCount}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>

                {filteredUsers.length > USER_PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                      disabled={userPage <= 1}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      ◀ 이전
                    </button>
                    <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {userPage} / {userPageCount}
                    </div>
                    <button
                      type="button"
                      onClick={() => setUserPage((p) => Math.min(userPageCount, p + 1))}
                      disabled={userPage >= userPageCount}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      다음 ▶
                    </button>
                  </div>
                )}
              </div>

              {/* 우측: 선택 사용자 상세 */}
              <div className="lg:col-span-8">
                {!selectedUser ? (
                  <div className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-gray-600">
                    왼쪽에서 사용자를 선택하세요.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* 사용자 헤더 */}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                      <div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {selectedUser.userId}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {selectedUser.companyName || "-"} · 담당 {selectedUser.userName || "-"}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={openNewCameraForm}
                          className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                        >
                          + 카메라 등록
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleChangeMaxCameraCount(selectedUser.userIdx, selectedUser.maxCameraCount)
                          }
                          className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
                        >
                          최대 {selectedUser.maxCameraCount}대 · 변경
                        </button>
                      </div>
                    </div>

                    {/* 카메라 목록 */}
                    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 lg:min-h-[392px]">
                      <div className="mb-3 text-sm font-bold text-gray-900 dark:text-white">
                        등록된 카메라 ({selectedUser.cameras.length}대)
                      </div>
                      {selectedUser.cameras.length === 0 ? (
                        <div className="py-6 text-center text-sm text-gray-400">
                          등록된 카메라가 없습니다.
                        </div>
                      ) : (
                        <>
                        <div className="space-y-2 lg:min-h-[336px] lg:content-start">
                          {pagedCameras.map((camera) => {
                              const activeNow = getEffectiveIsActive(camera);
                              const rtspUrl = makeRtspUrl(camera.streamKey);
                              return (
                                <div
                                  key={camera.camIdx}
                                  className="rounded-xl bg-gray-50 p-3 dark:bg-[#16202C]"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-xs font-bold text-gray-700 shadow-sm dark:bg-white/10 dark:text-gray-200">
                                        {camera.camNo}
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {camera.camTitle || "-"}
                                      </span>
                                      <span
                                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                          activeNow
                                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                            : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                        }`}
                                      >
                                        {activeNow ? "활성" : "비활성"}
                                      </span>
                                      <span
                                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                          camera.aiEnabled
                                            ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                                            : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                        }`}
                                      >
                                        {camera.aiEnabled ? "AI 켜짐" : "CCTV"}
                                      </span>
                                    </div>
                                    <div className="flex gap-1.5">
                                      <button
                                        type="button"
                                        onClick={() => handleEditCamera(camera)}
                                        className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
                                      >
                                        수정
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleActive(selectedUser.userIdx, camera)}
                                        className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
                                      >
                                        {activeNow ? "비활성" : "활성"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAi(selectedUser.userIdx, camera)}
                                        title={camera.aiEnabled ? "AI 끄기(순수 CCTV로 전환)" : "AI 켜기(안전장구 감지)"}
                                        className={`rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ${
                                          camera.aiEnabled
                                            ? "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300"
                                            : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
                                        }`}
                                      >
                                        {camera.aiEnabled ? "AI 끄기" : "AI 켜기"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteCamera(selectedUser.userIdx, camera)}
                                        className="rounded-md bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
                                      >
                                        삭제
                                      </button>
                                    </div>
                                  </div>

                                  <div className="mt-2 space-y-1.5">
                                    {camera.sourceRtsp && camera.sourceRtsp !== "." && (
                                      <CamUrlRow
                                        label="원본 RTSP"
                                        value={camera.sourceRtsp}
                                        onCopy={() => copyText(camera.sourceRtsp, "원본 RTSP 복사")}
                                      />
                                    )}
                                    <CamUrlRow
                                      label="시청 RTSP"
                                      value={rtspUrl}
                                      onCopy={() => copyText(rtspUrl, "RTSP 주소 복사")}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        {camListPageCount > 1 && (
                          <div className="mt-3 flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setCamListPage((p) => Math.max(1, p - 1))}
                              disabled={camListPage <= 1}
                              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                            >
                              ◀ 이전
                            </button>
                            <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                              {camListPage} / {camListPageCount}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setCamListPage((p) => Math.min(camListPageCount, p + 1))
                              }
                              disabled={camListPage >= camListPageCount}
                              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                            >
                              다음 ▶
                            </button>
                          </div>
                        )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* 카메라 등록 / 수정 모달 */}
      {showCamForm && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowCamForm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-[#1F2A3B]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {editingExistingCamera ? "카메라 수정" : "카메라 등록"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {selectedUser.userId}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs text-gray-600 dark:text-gray-300">
                카메라 번호
                <input
                  type="number"
                  min="1"
                  value={camNo}
                  onChange={(e) => setCamNo(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-300">
                카메라명
                <input
                  type="text"
                  value={camTitle}
                  onChange={(e) => setCamTitle(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2">
                RTSP 원본 주소 (선택)
                <input
                  type="text"
                  value={sourceRtsp}
                  onChange={(e) => setSourceRtsp(e.target.value)}
                  placeholder="rtsp://..."
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />
              </label>
            </div>

            {(selectedUserCameraLimitExceeded ||
              globalLicenseDeviceLimitExceeded ||
              !licenseInfo.ok) && (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                {!licenseInfo.ok
                  ? "라이선스가 정상 상태가 아니므로 신규 등록이 제한됩니다."
                  : globalLicenseDeviceLimitExceeded
                  ? "전체 라이선스 한도에 도달했습니다. (기존 카메라 수정은 가능)"
                  : "작업자 계정은 바디캠 1대만 등록할 수 있습니다."}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCamForm(false)}
                className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-gray-200"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSaveCamera}
                disabled={!canSaveCamera && !editingExistingCamera}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "저장 중..." : editingExistingCamera ? "수정 저장" : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

function CamUrlRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className="min-w-0 flex-1 break-all text-xs text-gray-700 dark:text-gray-300">
        {value}
      </span>
      {value && value !== "-" && (
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 rounded-md bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-600 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
        >
          복사
        </button>
      )}
    </div>
  );
}
